import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  promoErrorMessage,
  updatePromoCode,
  type PromoCode,
  type PromoStatus,
  type UpdatePromoInput,
} from '@/lib/promoApi';
import { dateTimeLocalToUnix, unixToDateTimeLocal } from '@/lib/promoDates';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  walletAddress: string;
  code: PromoCode | null;
  onSaved: () => void;
};

// 'expired' is a derived/lazy state, not an operator action — operators enable or disable.
const EDITABLE_STATUSES: PromoStatus[] = ['active', 'disabled'];

export const EditPromoCodeDialog: React.FC<Props> = ({ open, onOpenChange, walletAddress, code, onSaved }) => {
  const [percentOff, setPercentOff] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [status, setStatus] = useState<PromoStatus>('active');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!code) return;
    setPercentOff(String(code.percentOff));
    setMaxRedemptions(code.maxRedemptions ? String(code.maxRedemptions) : '');
    setExpiresAt(unixToDateTimeLocal(code.expiresAt));
    // A currently-expired code is re-enabled as 'active' if the operator picks Active.
    setStatus(code.status === 'disabled' ? 'disabled' : 'active');
  }, [code]);

  const handleSubmit = async () => {
    if (!code) return;

    const percent = Math.trunc(Number(percentOff));
    if (!Number.isFinite(percent) || percent < 1 || percent > 100) {
      toast.error('Discount must be a whole number between 1 and 100%.');
      return;
    }

    const updates: UpdatePromoInput = { percentOff: percent, status };

    // expiresAt: empty clears the expiry (backend treats 0/empty as "no expiry").
    if (expiresAt) {
      const unix = dateTimeLocalToUnix(expiresAt);
      if (unix == null) {
        toast.error('Enter a valid expiry date.');
        return;
      }
      updates.expiresAt = unix;
    } else {
      updates.expiresAt = 0;
    }

    if (maxRedemptions.trim()) {
      const max = Math.trunc(Number(maxRedemptions));
      if (!Number.isFinite(max) || max < 1) {
        toast.error('Max redemptions must be a positive whole number.');
        return;
      }
      updates.maxRedemptions = max;
    } else {
      updates.maxRedemptions = 0;
    }

    setSubmitting(true);
    try {
      await updatePromoCode(walletAddress, code.promoCodeId, updates);
      toast.success('Promo code updated');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(promoErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid='edit-promo-code-dialog'
        className='max-w-lg bg-[var(--pp-surface)] text-[var(--pp-text)]'
      >
        <DialogHeader>
          <DialogTitle>Edit promo code</DialogTitle>
          <DialogDescription>
            The code string is immutable — disable and create a new code to change it.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4'>
          <div className='grid gap-1.5'>
            <Label>Code</Label>
            <Input value={code?.code ?? ''} readOnly disabled className='bg-[var(--pp-subtle)] font-mono uppercase' />
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='grid gap-1.5'>
              <Label htmlFor='edit-promo-percent'>Discount %</Label>
              <Input
                id='edit-promo-percent'
                data-testid='edit-promo-percent'
                type='number'
                min={1}
                max={100}
                value={percentOff}
                onChange={(e) => setPercentOff(e.target.value)}
                className='bg-[var(--pp-subtle)]'
              />
            </div>
            <div className='grid gap-1.5'>
              <Label htmlFor='edit-promo-max'>Max redemptions</Label>
              <Input
                id='edit-promo-max'
                data-testid='edit-promo-max'
                type='number'
                min={1}
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                placeholder='Unlimited'
                className='bg-[var(--pp-subtle)]'
              />
            </div>
          </div>

          <div className='grid gap-1.5'>
            <Label htmlFor='edit-promo-expiry'>Expires</Label>
            <Input
              id='edit-promo-expiry'
              data-testid='edit-promo-expiry'
              type='datetime-local'
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className='bg-[var(--pp-subtle)]'
            />
            <p className='text-xs text-[var(--pp-text-subtle)]'>Clear the field to remove the expiry.</p>
          </div>

          <div className='grid gap-1.5'>
            <Label>Status</Label>
            <Select value={status} onValueChange={(value) => setStatus(value as PromoStatus)}>
              <SelectTrigger className='bg-[var(--pp-subtle)]' data-testid='edit-promo-status'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EDITABLE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === 'active' ? 'Active' : 'Disabled'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {code?.status === 'expired' && (
              <p className='text-xs text-[var(--pp-text-subtle)]'>
                This code lapsed past its expiry. Choosing Active re-enables it — update the expiry too.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type='button'
            className='gap-2'
            data-testid='edit-promo-submit'
            onClick={handleSubmit}
            disabled={submitting || !code}
            style={{ background: 'var(--pp-action)', color: '#fff' }}
          >
            <Save size={14} />
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPromoCodeDialog;
